import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Card, FormControlLabel, Grid, IconButton, Stack, TextField, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { LoadingButton } from '@mui/lab';
import isNumber from 'lodash/isNumber';

import CircleIcon from '@mui/icons-material/PanoramaFishEyeTwoTone';
import { useDictionary } from '@cvt/hooks/useDictionary';
import { FileUploader } from '@cvt/components/FileUploader';
import { imageMimes, videoMimes } from '@cvt/helpers/file';
import { DialogContext } from '@cvt/contexts';

import { DateInputFormat } from '@shared/helpers/date';
import { ErrorsAlert } from '@shared/components/ErrorsAlert';

import { getVideoThumbnail } from '../helpers/media';
import { TimelineEventPreview } from '../components/TimelineEventPreview';
import { datePickerRange } from '../components/PetDetailsManualForm';
import { CharacterCount } from '../components/CharacterCount';
import ImagePreviewGrid from '../components/ImagePreviewGrid';
import { fileSizeValidator } from '../helpers/validators';


type timelineEntry = {
  saved: Tributes.TimelineEvent;
  draft: Tributes.TimelineEvent;
}

interface Props {
  index: number;
  fieldPath: string;
  onRemove: (index: number) => void;
  isEditing?: boolean;
    isCreating?: boolean;
}

type MediaItem = File | Tributes.Media;

export const TimelineEventFieldArrayForm: FC<Props> = (props) => {
  const { index, fieldPath, onRemove, isEditing, isCreating } = props;
  const dictionary = useDictionary();
  const { watch } = useFormContext();
  const { openDialog } = useContext(DialogContext);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const timelineEvent: timelineEntry = watch(fieldPath);

  const [photoUrl, setPhotoUrl] = useState<string | undefined>();
  const [rejectedFiles, setRejectedFiles] = useState<File[]>([]);

  useEffect(() => {
    const updatePhotoUrl = async () => {
      const media = timelineEvent?.saved?.media;

      if (!media?.length) {
        setPhotoUrl(undefined);
        return;
      }

      const firstFile: MediaItem = media[0];

      if (firstFile instanceof File) {
        if (firstFile.type.startsWith('video/')) {
          try {
            const preview = await getVideoThumbnail(firstFile);
            setPhotoUrl(preview);
          } catch (err) {
            console.warn(`Could not generate thumbnail for ${firstFile.name}`, err);
            setPhotoUrl(URL.createObjectURL(firstFile));
          }
        } else {
          setPhotoUrl(URL.createObjectURL(firstFile));
        }
      } else if (typeof firstFile === 'object' && 'file' in firstFile) {
        setPhotoUrl(firstFile.file);
      }
    };

    updatePhotoUrl();
  }, [timelineEvent?.saved?.media]);

  const onViewSavedMedia = useCallback((selectedFileIndex?: number) => {
    const media = timelineEvent.saved.media || [];
    const getFile = (item: MediaItem) =>
      (item instanceof File)
        ? item
        : (typeof item === 'object' && 'file' in item
          ? item.file
          : undefined);

    const filesToView = media.map(getFile).filter(Boolean);

    let reorderedFiles = filesToView;
    if (isNumber(selectedFileIndex)) {
      const selectedFile = filesToView[selectedFileIndex];
      reorderedFiles = [selectedFile, ...filesToView.filter((_, idx) => idx !== selectedFileIndex)];
    }
    openDialog('mediaLightbox', { files: reorderedFiles });
  }, [openDialog, timelineEvent.saved.media]);

  return (
    <Grid container spacing={2} px={3}>
      <Grid size={12} className="card-holder" position="relative">
        <CircleIcon
          sx={{
            position: 'absolute',
            top: { xs: '24px', sm: '32px' },
            left: '-21px',
            color: '#fcdcda',
            '& path:first-child': { opacity: 1, color: '#fff' },
          }}
          fontSize="small"
        />
        <Card
          sx={{
            boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
            border: '1px solid rgb(254, 238, 237)',
            borderRadius: '12px',
            overflow: 'visible',
            p: { xs: 1, sm: 2 },
          }}
        >
          {(isEditing || isCreating) ? (
            <Grid container spacing={2}>
              <Grid size={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#263238' }}>
                    Date
                  </Typography>
                  <Tooltip title="Delete" placement="top">
                    <IconButton
                      color="primary"
                      onClick={() => onRemove(index)}
                    >
                      <DeleteOutline fontSize="small"/>
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Controller
                  name={`${fieldPath}.draft.date`}
                  rules={{
                    validate: (value) => {
                      if (value && value < datePickerRange.minDate) {
                        return dictionary.forms.validations.dateTooOld;
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      disableFuture
                      openTo="year"
                      views={['year', 'month', 'day']}
                      format={DateInputFormat}
                      maxDate={datePickerRange.maxDate}
                      minDate={datePickerRange.minDate}
                      onChange={(date) => {
                        field.onChange(date);
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                          sx: {
                            'fieldset': {
                              borderColor: '#e2e8f0',
                              borderRadius: '10px',
                            },
                          },
                        },
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <FormControlLabel
                  labelPlacement="top"
                  sx={{
                    alignItems: 'flex-start',
                    color: '#263238',
                    m: 0,
                    width: '100%',
                    '.MuiFormControlLabel-label': {
                      fontSize: 14,
                      fontWeight: 500,
                      mb: 1,
                    },
                  }}
                  slotProps={{
                    typography: {
                      fontSize: 12,
                      fontWeight: 400,
                    },
                  }}
                  control={
                    <Controller
                      name={`${fieldPath}.draft.text`}
                      rules={{
                        maxLength: {
                          value: 1000,
                          message: 'Maximum length is 1000 characters.',
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          color="primary"
                          fullWidth
                          multiline
                          minRows={5}
                          error={!!fieldState.error}
                          helperText={(
                            <Stack component="span" direction="row" justifyContent="space-between" width="100%" gap={2}>
                              <span>{fieldState.error?.message}</span>
                              <CharacterCount text={field.value} maxLength={1000} flexShrink={0} />
                            </Stack>
                          )}
                          placeholder="Share a cherished memory you have with your pet."
                          onChange={(event) => {
                            field.onChange(event);
                          }}
                          slotProps={{
                            input: {
                              sx: {
                                'fieldset': {
                                  borderColor: '#e2e8f0',
                                  borderRadius: '10px',
                                },
                                resize: 'vertical',
                              },
                            },
                          }}
                        />
                      )}
                    />
                  }
                  label="Memory"
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name={`${fieldPath}.draft.media`}
                  render={({ field }) => (
                    <ImagePreviewGrid
                      files={field.value || []}
                      onRemove={(fileToRemove) => {
                        const updatedFiles = field.value?.filter((file: File) => file !== fileToRemove);
                        field.onChange(updatedFiles || []);
                      }}
                      title="Photos"
                      slotProps={isMobile ?  {
                        paper: {
                          sx: {
                            width: 70,
                            height: 70,
                          },
                        },
                      } : undefined}
                    />
                  )}
                />

                {rejectedFiles.length > 0 && (
                  <ErrorsAlert
                    sx={{ my: 2 }}
                    errorMessages={rejectedFiles.map((file) => `${file.name}: ${dictionary.forms.validations.invalidFileSize('10 MB')}`)}
                    title={`Rejected file${rejectedFiles.length > 1 ? 's' : ''}`}
                    centered
                  />
                )}

                <Controller
                  name={`${fieldPath}.draft.media`}
                  render={({ field }) => (
                    <FileUploader
                      name={`${fieldPath}.draft.media`}
                      label="Photos"
                      validator={fileSizeValidator}
                      onFilesRejected={setRejectedFiles}
                      inputProps={{
                        accept: [...imageMimes, ...videoMimes].join(','),
                        multiple: true,
                      }}
                      buttonRender={() => (
                        <LoadingButton
                          variant="outlined"
                          color="primary"
                          component="span"

                          size="small"
                          sx={{
                            bgcolor: 'white',
                            px: 3,
                            py: 1,
                            mt: 3,
                          }}
                          startIcon={<Add />}
                        >
                          Add Photos / Videos
                        </LoadingButton>

                      )}
                      onChange={(newFiles: File[]) => {
                        const updatedFiles = [...field.value, ...newFiles];
                        field.onChange(updatedFiles);
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          ) : (
            <TimelineEventPreview
              timelineEvent={timelineEvent.saved}
              onViewMedia={onViewSavedMedia}
            />
          )}
        </Card>
      </Grid>
    </Grid>
  );
};
